import { Dispatch, useCallback, useEffect, useMemo, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useLocation, useNavigate, useNavigationType } from 'react-router';
import { Link } from '@mui/material';
import classNames from 'classnames';
import { Action } from 'redux';

import { AGE_OF_MAJORITY, MARKET_DATA_DATE } from '../../../lib/constants/date-time.constants';
import {
  CallStatus,
  CryptocurrenciesEnum,
  CurrencyEnum,
  PaymentProviderEnum,
} from '../../../lib/enums';
import lang from '../../../lib/language';
import {
  CouponPaymentParams,
  CouponPreviewParams,
  CreatePlanPaymentParams,
  CreatePurchasePaymentParam,
  ModifyOwnBirthdateRequest,
  PurchasePaymentTypeEnum,
  TradingPlan,
} from '../../../lib/models/crm/types';
import { RootState } from '../../../lib/store';
import {
  couponPreview as couponPreviewCall,
  createPlanPayment,
  createPurchasePayment as createPurchasePaymentCall,
  modifyOwnBirthdate as modifyOwnBirthdateCall,
  resetCouponPreview as resetCouponPreviewCall,
} from '../../../lib/store/crm';
import { getCurrentAccountTradingPlan, preparePaymentURLs } from '../../../lib/store/crm/helpers';
import { CRMCall } from '../../../lib/store/crm/types';
import { hasValidCookie, isCookieCheckPending } from '../../../lib/store/selectors';
import { formatCurrencyBySymbol, getCurrencySymbolByCurrencyCode } from '../../../lib/util/DataHelpers';
import {
  getMaxAllowedDateByAge,
  isUnderAge,
  toMomentDate,
} from '../../../lib/util/DateTimeHelpers';

import IconCloseWhite from '../../../assets/img/icon-close-white.svg';
import InfoIcon from '../../../assets/img/info-circle-blue.svg';
import StripeLogo from '../../../assets/img/stripe-logo.svg';
import { ClickableImage } from '../../../components';
import CheckboxControlV2 from '../../../components/CheckboxControl/CheckboxControlV2';
import LogoAndCloseButton from '../../../components/LogoAndCloseButton/LogoAndCloseButton';
import PaymentCancelModal from '../../../components/PaymentCancelModal/PaymentCancelModal';
import RadioGroupOption from '../../../components/RadioGroup/RadioGroupOption/RadioGroupOption';
import SingleDatePickerWrapper from '../../../components/SingleDatePickerWrapper/SingleDatePickerWrapper';
import config from '../../../config';
import { useIsMobile } from '../../../hooks';
import Button from '../../../shared_components/Button/Button';
import ClickableDiv from '../../../shared_components/ClickableDiv/ClickableDiv';
import { UI_ROUTES } from '../../../util/routes';

import CouponCode from './components/CouponCode/CouponCode';
import SelectedPlan from './components/SelectedPlan/SelectedPlan';
import {
  buildPlanPaymentSuccessPath,
  buildPurchasePaymentSuccessPath,
} from './paymentMethodSelection.helpers';

import styles from './PaymentMethodSelection.module.scss';

const IDEMPOTENCY_KEY_LENGTH = 128;

const generateIdempotencyKey = (): string => {
  const bytes = new Uint8Array(IDEMPOTENCY_KEY_LENGTH / 2);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
};

const PaymentMethodSelection = (props: ReduxProps): JSX.Element => {
  const {
    isCookieValid,
    isCookieCheckRunning,
    createPayment,
    individualId,
    isPlanPaymentsReady,
    paymentGatewayURL,
    paymentResponseData,
    isPurchasePaymentReady,
    createPurchasePayment,
    existingBirthdate,
    modifyOwnBirthdate,
    modifyOwnBirthdateStatus,
    userCorrelationId,
    tradingPlans,
    previewCoupon,
    couponPreviewStatus,
    couponPreviewData,
    couponPreviewError,
    resetCoupon,
    currentTradingPlan,
  } = props;

  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const location = useLocation();
  const { isMobile } = useIsMobile();

  const [paymentMethod, setPaymentMethod] = useState<PaymentProviderEnum>(PaymentProviderEnum.Unspecified);
  const [isPaymentTermsAndConditionsChecked, setIsPaymentTermsAndConditionsChecked] = useState<boolean>(false);
  const [showCancelModal, setShowCancelModal] = useState<boolean>(false);
  const [isCryptoPending, setIsCryptoPending] = useState<boolean>(false);
  const [isFreePaymentPending, setIsFreePaymentPending] = useState<boolean>(false);
  const [appliedCouponCode, setAppliedCouponCode] = useState<string | null>(null);
  const [isPlanDetailsOpen, setIsPlanDetailsOpen] = useState<boolean>(false);

  const planId = location.state?.planId;
  const accountId = location.state?.accountId;
  const prevPage = location.state?.prevPage;
  const isFromEnroll = location.state?.isEnroll;
  const purchaseType = location.state?.purchaseType;
  const isResetDailyLoss = PurchasePaymentTypeEnum.AccountDailyLossRewind === purchaseType;
  const isAutoPass = PurchasePaymentTypeEnum.AutoPass === purchaseType;
  const withAutoPass = location.state?.withAutoPass ?? false;

  const couponPurchaseType = useMemo<PurchasePaymentTypeEnum>(() => {
    if (isResetDailyLoss) {
      return PurchasePaymentTypeEnum.AccountDailyLossRewind;
    }

    if (isAutoPass) {
      return PurchasePaymentTypeEnum.AutoPass;
    }

    return withAutoPass ? PurchasePaymentTypeEnum.PlanWithAutoPass : PurchasePaymentTypeEnum.Plan;
  }, [isResetDailyLoss, isAutoPass, withAutoPass]);

  const isAccountCouponPurchase = isResetDailyLoss || isAutoPass;

  const buildCouponPaymentParams = useCallback((): CouponPaymentParams => {
    if (!couponPreviewData || !appliedCouponCode) {
      return {};
    }

    return {
      coupon_code: appliedCouponCode,
      quote_reference: couponPreviewData.quote_reference,
      idempotency_key: generateIdempotencyKey(),
    };
  }, [couponPreviewData, appliedCouponCode]);

  const [isBirthdateStep, setIsBirthdateStep] = useState<boolean>(false);
  const [pendingBirthdateUpdate, setPendingBirthdateUpdate] = useState<boolean>(false);
  const [isProcessingBirthdatePayment, setIsProcessingBirthdatePayment] = useState<boolean>(false);

  const selectedPlan = useMemo<TradingPlan | undefined>(
    () => tradingPlans.find((plan) => plan.id === planId),
    [tradingPlans, planId],
  );

  const initialBirthdateValue = useMemo(
    () => toMomentDate(existingBirthdate)?.format(MARKET_DATA_DATE) ?? null,
    [existingBirthdate],
  );

  const [birthdateValue, setBirthdateValue] = useState<string | null>(initialBirthdateValue);
  const [birthdateError, setBirthdateError] = useState<string | null>(null);

  useEffect(() => {
    resetCoupon();
  }, []);

  useEffect(() => {
    const redirectUrl = sessionStorage.getItem('paymentGatewayURL');
    if (redirectUrl) {
      // If the user hit browser back from Stripe, redirect to cancel screen.
      navigate(UI_ROUTES.paymentCancel, { replace: true });
      return;
    }

    // Clear any stale gateway URL on new attempts so we don't flash cancel.
    if (navigationType === 'PUSH' || navigationType === 'REPLACE') {
      sessionStorage.removeItem('paymentGatewayURL');
    }
  }, []);

  const isBirthdateUnderEighteen = useMemo(() => {
    if (!birthdateValue) {
      return false;
    }

    return isUnderAge(birthdateValue);
  }, [birthdateValue]);

  useEffect(() => {
    // While the cookie session check is still running we cannot tell whether the user is
    // logged in, so do not bounce them out yet.
    if (isCookieCheckRunning) {
      return;
    }

    if (
      (!planId && !isAutoPass && !isResetDailyLoss)
      || (!accountId && isResetDailyLoss)
      || !isCookieValid
    ) {
      // During enrollment the user is not authenticated yet, so the products/overview screens
      // are not part of the router and would fall through to the sign-up screen.
      const fallbackRoute = isFromEnroll ? UI_ROUTES.enrollPhoneVerified : (prevPage ?? UI_ROUTES.overview);
      navigate(fallbackRoute, { replace: true });
    }
  }, [planId, accountId, isResetDailyLoss, prevPage, isCookieValid, isCookieCheckRunning, isFromEnroll]);

  useEffect(() => {
    if (isCryptoPending || isFreePaymentPending) return;
    if (isPurchasePaymentReady && paymentGatewayURL && (isResetDailyLoss || isAutoPass)) {
      sessionStorage.setItem('paymentGatewayURL', paymentGatewayURL);
      window.open(paymentGatewayURL, '_self');
    }
  }, [isPurchasePaymentReady, paymentGatewayURL, isResetDailyLoss, isAutoPass]);

  useEffect(() => {
    if (isCryptoPending || isFreePaymentPending) return;
    if (isPlanPaymentsReady && paymentGatewayURL && !isResetDailyLoss && !isAutoPass) {
      sessionStorage.setItem('paymentGatewayURL', paymentGatewayURL);
      window.open(paymentGatewayURL, '_self');
    }
  }, [isPlanPaymentsReady, paymentGatewayURL, isResetDailyLoss]);

  useEffect(() => {
    if (!isFreePaymentPending || isResetDailyLoss || isAutoPass) return;
    if (isPlanPaymentsReady) {
      setIsFreePaymentPending(false);
      // Hard reload (replace) so the app re-bootstraps and the URL-driven plan-callback polling
      // starts, while keeping the payment screen out of the back-history.
      window.location.replace(
        buildPlanPaymentSuccessPath({
          withAutoPass,
          planId,
          paymentReference: paymentResponseData.payment_reference,
        }),
      );
    }
  }, [isFreePaymentPending, isPlanPaymentsReady, isResetDailyLoss, isAutoPass]);

  useEffect(() => {
    if (!isFreePaymentPending || !(isResetDailyLoss || isAutoPass)) return;
    if (isPurchasePaymentReady) {
      setIsFreePaymentPending(false);
      window.location.replace(
        buildPurchasePaymentSuccessPath({
          purchaseType: isResetDailyLoss
            ? PurchasePaymentTypeEnum.AccountDailyLossRewind
            : PurchasePaymentTypeEnum.AutoPass,
          paymentReference: paymentResponseData.payment_reference,
        }),
      );
    }
  }, [isFreePaymentPending, isPurchasePaymentReady, isResetDailyLoss, isAutoPass]);

  useEffect(() => {
    if (!isCryptoPending) return;
    const isPurchaseDone = isPurchasePaymentReady && (isResetDailyLoss || isAutoPass);
    const isPlanDone = isPlanPaymentsReady && !isResetDailyLoss && !isAutoPass;
    if (isPlanDone) {
      setIsCryptoPending(false);
      navigate(UI_ROUTES.cryptoPayment, {
        state: {
          ...paymentResponseData,
          selectedPlan,
          withAutoPass,
          couponResult: appliedCoupon ? couponPreviewData : null,
          appliedCouponCode: appliedCoupon,
        },
      });
    }
    if (isPurchaseDone) {
      setIsCryptoPending(false);
      navigate(UI_ROUTES.cryptoPayment, {
        state: {
          ...paymentResponseData,
          isResetDailyLossPayment: isResetDailyLoss,
          isDeactivationOfAutoPass: isAutoPass,
        },
      });
    }
  }, [isCryptoPending, isPurchasePaymentReady, isPlanPaymentsReady, paymentResponseData]);

  useEffect(() => {
    setBirthdateValue(initialBirthdateValue);
    if (initialBirthdateValue) {
      setBirthdateError(null);
      setPendingBirthdateUpdate(false);
    }
  }, [initialBirthdateValue]);

  useEffect(() => {
    if (paymentMethod !== PaymentProviderEnum.Bvnk) {
      setIsBirthdateStep(false);
      setBirthdateError(null);
      setBirthdateValue(initialBirthdateValue);
      setPendingBirthdateUpdate(false);
      setIsProcessingBirthdatePayment(false);
    }
  }, [paymentMethod, initialBirthdateValue]);

  useEffect(() => {
    if (!pendingBirthdateUpdate) return;

    if (modifyOwnBirthdateStatus === CallStatus.READY) {
      setPendingBirthdateUpdate(false);
      setBirthdateError(null);

      if (isResetDailyLoss) {
        createPurchasePayment({
          account_id: accountId,
          individual_id: individualId as number,
          payment_provider: paymentMethod,
          purchase_type: PurchasePaymentTypeEnum.AccountDailyLossRewind,
          ...buildCouponPaymentParams(),
          ...preparePaymentURLs(
            PurchasePaymentTypeEnum.AccountDailyLossRewind,
            UI_ROUTES.dashboard,
            UI_ROUTES.dashboard,
            UI_ROUTES.dashboard,
          ),
        });
        return;
      }

      if (isAutoPass) {
        createPurchasePayment({
          account_id: accountId,
          individual_id: individualId as number,
          payment_provider: paymentMethod,
          purchase_type: PurchasePaymentTypeEnum.AutoPass,
          ...buildCouponPaymentParams(),
          ...preparePaymentURLs(
            PurchasePaymentTypeEnum.AutoPass,
            UI_ROUTES.planDetails,
            UI_ROUTES.paymentCancel,
            UI_ROUTES.planDetails,
          ),
        });
        return;
      }

      createPayment({
        individual_id: individualId as number,
        trading_plan_id: planId,
        payment_provider: paymentMethod,
        with_auto_pass: withAutoPass,
        ...buildCouponPaymentParams(),
        ...preparePaymentURLs(
          withAutoPass ? PurchasePaymentTypeEnum.PlanWithAutoPass : PurchasePaymentTypeEnum.Plan,
          UI_ROUTES.paymentSuccess,
          UI_ROUTES.paymentCancel,
          UI_ROUTES.paymentError,
          planId,
        ),
      });
    } else if (modifyOwnBirthdateStatus === CallStatus.ERROR) {
      setPendingBirthdateUpdate(false);
      setBirthdateError(lang.paymentMethodBirthdateSubmitError());
      setIsProcessingBirthdatePayment(false); // allow retry on error
    }
  }, [
    modifyOwnBirthdateStatus,
    pendingBirthdateUpdate,
    createPayment,
    createPurchasePayment,
    accountId,
    individualId,
    paymentMethod,
    planId,
    isResetDailyLoss,
    isAutoPass,
    withAutoPass,
    buildCouponPaymentParams,
  ]);

  const needsBirthdateCapture = paymentMethod === PaymentProviderEnum.Bvnk
    && (!existingBirthdate || existingBirthdate.trim().length === 0);

  const rightColumnTitle = useMemo(() => {
    if (isResetDailyLoss) {
      return lang.cryptoPaymentDailyLossRewindTitle();
    }

    if (isAutoPass) {
      return lang.paymentMethodAutoScaleTitle();
    }

    return lang.cryptoPaymentSelectedPlanTitle();
  }, [isResetDailyLoss, isAutoPass]);

  const priceLabel = isResetDailyLoss || isAutoPass
    ? lang.cryptoPaymentTotalPrice()
    : lang.cryptoPaymentPlanPriceLabel();

  const hasAppliedCoupon = !!couponPreviewData;

  const price = useMemo(() => {
    if (isResetDailyLoss) {
      return {
        amount: currentTradingPlan?.rewind_daily_loss_price ?? null,
        currencySymbol: getCurrencySymbolByCurrencyCode(
          currentTradingPlan?.rewind_daily_loss_price_ccy ?? null,
        ),
      };
    }

    if (isAutoPass) {
      return {
        amount: currentTradingPlan?.auto_pass_deactivation_price ?? null,
        currencySymbol: getCurrencySymbolByCurrencyCode(
          currentTradingPlan?.auto_pass_deactivation_ccy || CurrencyEnum.USD,
        ),
      };
    }

    if (!selectedPlan) {
      return { amount: null, currencySymbol: '' };
    }

    return {
      amount: withAutoPass ? selectedPlan.plan_price_with_auto_pass : selectedPlan.plan_price,
      currencySymbol: getCurrencySymbolByCurrencyCode(selectedPlan.plan_price_ccy),
    };
  }, [isResetDailyLoss, isAutoPass, currentTradingPlan, selectedPlan, withAutoPass]);

  const priceCurrencySymbol = couponPreviewData
    ? getCurrencySymbolByCurrencyCode(couponPreviewData.currency_code)
    : price.currencySymbol;

  const appliedCoupon = couponPreviewData ? appliedCouponCode : null;
  const isFreePayment = !!couponPreviewData && couponPreviewData.final_amount === 0;
  const isFreePlan = isFreePayment && !isResetDailyLoss && !isAutoPass;

  useEffect(() => {
    if (isFreePayment) {
      setPaymentMethod(PaymentProviderEnum.Unspecified);
    }
  }, [isFreePayment]);

  const handleSubmit = () => {
    if (!individualId) {
      return;
    }

    // Uncomment this block when bussiness is ready for BVNK payments
    // if (isBirthdateStep) {
    //   if (!birthdateValue) {
    //     setBirthdateError(lang.commonOnboardingPersonalDetailsDateOfBirthErrorMessage());
    //     return;
    //   }

    //   if (isBirthdateUnderEighteen) {
    //     setBirthdateError(lang.commonOnboardingPersonalDetailsUnderEighteenError());
    //     return;
    //   }

    //   if (modifyOwnBirthdateStatus !== CallStatus.PENDING) {
    //     if (!userCorrelationId) {
    //       setBirthdateError(lang.paymentMethodBirthdateSubmitError());
    //       return;
    //     }
    //     setBirthdateError(null);
    //     setPendingBirthdateUpdate(true);
    //     setIsProcessingBirthdatePayment(true);

    //     modifyOwnBirthdate({
    //       birthdate: birthdateValue,
    //       user_id: userCorrelationId,
    //     });
    //   }
    //   return;
    // }
    // if (needsBirthdateCapture) {
    //   setIsBirthdateStep(true);
    //   setBirthdateError(null);
    //   return;
    // }

    const isCrypto = paymentMethod === PaymentProviderEnum.Solana;
    if (isCrypto) {
      setIsCryptoPending(true);
    }
    if (isFreePayment) {
      setIsFreePaymentPending(true);
    }
    const cryptoSymbol = isCrypto ? { crypto_symbol: CryptocurrenciesEnum.USDT } : {};

    if (isResetDailyLoss) {
      createPurchasePayment({
        account_id: accountId,
        individual_id: individualId as number,
        payment_provider: isFreePayment ? null : paymentMethod,
        purchase_type: PurchasePaymentTypeEnum.AccountDailyLossRewind,
        ...cryptoSymbol,
        ...buildCouponPaymentParams(),
        ...(!isCrypto
          && preparePaymentURLs(
            PurchasePaymentTypeEnum.AccountDailyLossRewind,
            UI_ROUTES.dashboard,
            UI_ROUTES.dashboard,
            UI_ROUTES.dashboard,
          )),
      });
      return;
    }

    if (isAutoPass) {
      createPurchasePayment({
        account_id: accountId,
        individual_id: individualId as number,
        payment_provider: isFreePayment ? null : paymentMethod,
        purchase_type: PurchasePaymentTypeEnum.AutoPass,
        ...cryptoSymbol,
        ...buildCouponPaymentParams(),
        ...(!isCrypto
          && preparePaymentURLs(
            PurchasePaymentTypeEnum.AutoPass,
            UI_ROUTES.planDetails,
            UI_ROUTES.paymentCancel,
            UI_ROUTES.planDetails,
          )),
      });
      return;
    }

    createPayment({
      individual_id: individualId as number,
      trading_plan_id: planId,
      payment_provider: isFreePayment ? null : paymentMethod,
      with_auto_pass: withAutoPass,
      terms_and_conditions_url: config.links.paymentTermsAndConditionsUrl,
      ...cryptoSymbol,
      ...buildCouponPaymentParams(),
      ...(!isCrypto
        && preparePaymentURLs(
          withAutoPass ? PurchasePaymentTypeEnum.PlanWithAutoPass : PurchasePaymentTypeEnum.Plan,
          UI_ROUTES.paymentSuccess,
          UI_ROUTES.paymentCancel,
          UI_ROUTES.paymentError,
          planId,
        )),
    });
  };

  const isPlanStepSubmitDisabled = isFreePayment
    ? !isPaymentTermsAndConditionsChecked
    : !isPaymentTermsAndConditionsChecked || !paymentMethod;

  const isSubmitDisabled = isBirthdateStep
    ? !birthdateValue
      || modifyOwnBirthdateStatus === CallStatus.PENDING
      || isBirthdateUnderEighteen
      || isProcessingBirthdatePayment
    : isPlanStepSubmitDisabled;

  const continueButton = (
    <Button
      fullWidth
      id="createAccount"
      ariaLabel="create account"
      onClick={handleSubmit}
      disabled={isSubmitDisabled}
      className={styles.continueButton}
      biggerFontSize
    >
      {isFreePlan ? lang.paymentMethodSelectedPlanGetFreePlan() : lang.commonContinueButton()}
    </Button>
  );

  return (
    <div className={styles.container}>
      <LogoAndCloseButton
        displayCloseButton
        newWebGoBackButtonDesign
        displayBackButton={isBirthdateStep}
        onBackBtnPress={() => {
          setIsBirthdateStep(false);
          setBirthdateError(null);
          setPendingBirthdateUpdate(false);
          setBirthdateValue(initialBirthdateValue);
          setIsProcessingBirthdatePayment(false);
        }}
        onClose={() => setShowCancelModal(true)}
      />

      {isMobile && !isBirthdateStep && (
        <div className={styles.mobileClose}>
          <ClickableImage
            alt=""
            src={IconCloseWhite}
            onClick={() => setShowCancelModal(true)}
          />
        </div>
      )}

      <div className={styles.wrapper}>
        {isBirthdateStep ? (
          <div className={styles.card}>
            <div className={styles.birthdateContainer}>
              <h3 className={styles.birthdateTitle}>{lang.paymentMethodBirthdateTitle()}</h3>
              <p className={styles.birthdateDescription}>
                {lang.paymentMethodBirthdateDescription()}
              </p>
              <p className={styles.birthdateLabel}>
                {lang.commonOnboardingPersonalDetailsDateOfBirth()}
              </p>
              <SingleDatePickerWrapper
                label={lang.commonOnboardingPersonalDetailsDateOfBirth()}
                value={birthdateValue}
                maxDate={getMaxAllowedDateByAge(AGE_OF_MAJORITY).format(MARKET_DATA_DATE)}
                minDate="1900-01-01"
                className={styles.birthdateDatepicker}
                onDateChange={(value) => {
                  const formatted = toMomentDate(value)?.format(MARKET_DATA_DATE) ?? null;
                  setBirthdateValue(formatted);
                  if (formatted) {
                    const underEighteen = isUnderAge(formatted);
                    setBirthdateError(
                      underEighteen
                        ? lang.commonOnboardingPersonalDetailsUnderEighteenError()
                        : null,
                    );
                  } else {
                    setBirthdateError(null);
                  }
                }}
              />
              {birthdateError && <span className={styles.birthdateError}>{birthdateError}</span>}
            </div>
            {!isMobile && continueButton}
          </div>
        ) : (
          <>
            <div className={styles.card}>
              <div className={styles.row}>
                <span className={styles.title}>{lang.payamentMethodTitle()}</span>
              </div>
              <div className={styles.row}>
                <span className={classNames(styles.subtitle, { [styles.subtitleWeb]: !isMobile })}>
                  {lang.payamentMethodSubtitle()}
                </span>
              </div>
              <ClickableDiv
                onClick={isFreePayment ? undefined : () => setPaymentMethod(PaymentProviderEnum.Stripe)}
                className={classNames(styles.optionRow, { [styles.optionRowDisabled]: isFreePayment })}
              >
                <div className={styles.logoAndRadio}>
                  <img src={StripeLogo} alt="" />
                  <RadioGroupOption
                    id="stripe-payment-option"
                    value={paymentMethod}
                    active={paymentMethod === PaymentProviderEnum.Stripe}
                  />
                </div>
                <span
                  className={classNames(styles.paymentTypeDesc, {
                    [styles.paymentTypeDescMob]: isMobile,
                  })}
                >
                  {lang.payamentMethodCreditCardDesc()}
                </span>
              </ClickableDiv>

              <ClickableDiv
                onClick={isFreePayment ? undefined : () => setPaymentMethod(PaymentProviderEnum.Solana)}
                className={classNames(styles.optionRow, { [styles.optionRowDisabled]: isFreePayment })}
              >
                <div className={styles.logoAndRadio}>
                  <div className={styles.cryptoTitle}>{lang.paymentMethodCrypto()}</div>
                  <RadioGroupOption
                    id="crypto-payment-option"
                    value={paymentMethod}
                    active={paymentMethod === PaymentProviderEnum.Solana}
                  />
                </div>
                <span
                  className={classNames(styles.paymentTypeDesc, {
                    [styles.paymentTypeDescMob]: isMobile,
                  })}
                >
                  {lang.paymentMethodCryptoDesc()}
                </span>
              </ClickableDiv>

              <div className={styles.row}>
                <CheckboxControlV2
                  id="paymentTermsAndConditions"
                  errorId="paymentTermsAndConditionsError"
                  checked={isPaymentTermsAndConditionsChecked}
                  centered={false}
                  className={styles.checkbox}
                  onChange={(value) => {
                    setIsPaymentTermsAndConditionsChecked(value);
                  }}
                  component={(
                    <p className={classNames(styles.text, { [styles.policyText]: !isMobile })}>
                      {lang.payamentMethodAgreementText1()}
                      <Link
                        className={classNames(styles.policyButton, styles.policyLink, {
                          [styles.inlinePolicyButton]: !isMobile,
                        })}
                        target="_blank"
                        href={config.links.paymentTermsAndConditionsUrl}
                      >
                        {lang.payamentMethodAgreementText2()}
                      </Link>
                    </p>
                  )}
                />
              </div>
              {!isMobile && continueButton}
            </div>
            <div className={styles.rightColumn}>
              <div className={styles.columnTitleRow}>
                <div className={styles.columnTitle}>{rightColumnTitle}</div>
                {selectedPlan && isMobile && (
                  <ClickableImage
                    src={InfoIcon}
                    alt=""
                    className={styles.infoIcon}
                    onClick={() => setIsPlanDetailsOpen(true)}
                  />
                )}
              </div>
              {selectedPlan && (
              <SelectedPlan
                selectedPlan={selectedPlan}
                withAutoPass={withAutoPass}
                isDetailsOpen={isPlanDetailsOpen}
                onCloseDetails={() => setIsPlanDetailsOpen(false)}
              />
              )}
              <div className={styles.planPriceRow}>
                <div className={styles.planPriceLabelWrapper}>
                  <div className={styles.planPriceLabel}>{priceLabel}</div>
                  {hasAppliedCoupon && (
                    <div className={styles.discountAppliedLabel}>
                      {lang.paymentMethodSelectedPlanDiscountApplied()}
                    </div>
                  )}
                </div>
                <div className={styles.planPriceValueWrapper}>
                  {!isResetDailyLoss && !isAutoPass && (
                    <div className={styles.planPriceSuffix}>
                      {lang.cryptoPaymentPlanPriceSuffix()}
                    </div>
                  )}
                  {hasAppliedCoupon ? (
                    <div className={styles.discountedPriceRow}>
                      <div className={styles.originalPrice}>
                        {formatCurrencyBySymbol(
                          couponPreviewData.original_amount,
                          priceCurrencySymbol,
                        )}
                      </div>
                      <div className={styles.planPriceValue}>
                        {formatCurrencyBySymbol(
                          couponPreviewData.final_amount,
                          priceCurrencySymbol,
                        )}
                      </div>
                    </div>
                  ) : (
                    price.amount !== null && (
                      <div className={styles.planPriceValue}>
                        {formatCurrencyBySymbol(price.amount, priceCurrencySymbol)}
                      </div>
                    )
                  )}
                </div>
              </div>
              <CouponCode
                hasAppliedCoupon={hasAppliedCoupon}
                isApplyingCoupon={couponPreviewStatus === CallStatus.PENDING}
                couponError={couponPreviewError}
                purchaseType={couponPurchaseType}
                onRemoveCoupon={() => {
                  resetCoupon();
                  setAppliedCouponCode(null);
                }}
                onClearCouponError={() => {
                  resetCoupon();
                  setAppliedCouponCode(null);
                }}
                onApplyCoupon={(couponCode) => {
                  if (!individualId || (isAccountCouponPurchase ? !accountId : !planId)) {
                    return;
                  }
                  const isCrypto = paymentMethod === PaymentProviderEnum.Solana;
                  setAppliedCouponCode(couponCode);
                  previewCoupon({
                    individual_id: individualId,
                    coupon_code: couponCode,
                    purchase_type: couponPurchaseType,
                    with_auto_pass: withAutoPass,
                    payment_provider: null,
                    ...(isAccountCouponPurchase
                      ? { account_id: accountId }
                      : { trading_plan_id: planId }),
                    ...(isCrypto ? { crypto_symbol: CryptocurrenciesEnum.USDT } : {}),
                  });
                }}
              />
            </div>
          </>
        )}
      </div>

      {isMobile && <div className={styles.mobileFooter}>{continueButton}</div>}

      {showCancelModal && (
        <PaymentCancelModal
          isFromEnroll={isFromEnroll}
          closeModal={() => setShowCancelModal(false)}
        />
      )}
    </div>
  );
};

const mapDispatchToProps = (dispatch: Dispatch<Action>) => ({
  createPayment: (params: CreatePlanPaymentParams) => dispatch(createPlanPayment(params)),
  createPurchasePayment: (payload: CreatePurchasePaymentParam) =>
    dispatch(createPurchasePaymentCall(payload)),
  modifyOwnBirthdate: (payload: ModifyOwnBirthdateRequest) =>
    dispatch(modifyOwnBirthdateCall(payload)),
  previewCoupon: (payload: CouponPreviewParams) => dispatch(couponPreviewCall(payload)),
  resetCoupon: () => dispatch(resetCouponPreviewCall()),
});

const mapStateToProps = (state: RootState) => ({
  isCookieValid: hasValidCookie(state),
  isCookieCheckRunning: isCookieCheckPending(state),
  individualId: state.crm.individualOrCompanyEmployeeId,
  paymentGatewayURL: state.crm.paymentResponseData.gateway_url,
  paymentResponseData: state.crm.paymentResponseData,
  isPurchasePaymentReady:
    state.crm.statusByCall[CRMCall.createPurchasePayment]?.status === CallStatus.READY,
  isPlanPaymentsReady:
    state.crm.statusByCall[CRMCall.createPlanPayment]?.status === CallStatus.READY,
  modifyOwnBirthdateStatus:
    state.crm.statusByCall[CRMCall.modifyOwnBirthdate]?.status ?? CallStatus.INITIAL,
  couponPreviewStatus:
    state.crm.statusByCall[CRMCall.couponPreview]?.status ?? CallStatus.INITIAL,
  couponPreviewData: state.crm.couponPreviewData,
  couponPreviewError: state.crm.couponPreviewError,
  existingBirthdate: state.crm.individualExtendedInfo?.birth_date ?? '',
  userCorrelationId: state.crm.individualExtendedInfo?.user_correlation_id,
  tradingPlans: state.crm.tradingPlans,
  currentTradingPlan: getCurrentAccountTradingPlan(state.crm),
});

const connector = connect(mapStateToProps, mapDispatchToProps);

type ReduxProps = ConnectedProps<typeof connector>;

export default connector(PaymentMethodSelection);
